<template>
  <div class="p-6 dark:text-gray-900">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
        {{ isAccessAttempt ? "Access Attempt Details" : "Access Log Details" }}
      </h1>
      <button
        @click="goBack"
        class="btn bg-gray-200 text-gray-700 hover:bg-gray-300"
      >
        Back to List
      </button>
    </div>

    <!-- Error Message -->
    <div
      v-if="error"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
      role="alert"
    >
      <strong class="font-bold">Error!</strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <!-- Loading Message -->
    <div v-if="isLoading" class="text-gray-600 mb-4">
      Loading {{ isAccessAttempt ? "access attempt" : "access log" }} details...
    </div>

    <!-- Access Log/Attempt Details -->
    <div
      v-if="!isLoading && !error && log"
      class="glass-card p-6"
    >
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="col-span-2">
          <h2 class="text-xl font-semibold mb-2 dark:text-gray-50">Basic Information</h2>
        </div>
        <div>
          <p class="text-sm text-gray-600 dark:text-gray-50">Timestamp</p>
          <p class="font-mediu dark:text-gray-50">{{ formatDateTime(log.attempt_time) }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-600 dark:text-gray-50">Username</p>
          <p class="font-medium dark:text-gray-50">{{ log.username || "N/A" }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-600 dark:text-gray-50">IP Address</p>
          <p class="font-medium dark:text-gray-50">{{ log.ip_address }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-600 dark:text-gray-50">Path</p>
          <p class="font-medium dark:text-gray-50">{{ log.path_info }}</p>
        </div>
        <div class="col-span-2">
          <p class="text-sm text-gray-600 dark:text-gray-50">User Agent</p>
          <p class="font-medium dark:text-gray-50">{{ log.user_agent }}</p>
        </div>
        <div v-if="isAccessAttempt">
          <p class="text-sm text-gray-600 dark:text-gray-50">Failures Since Start</p>
          <p
            :class="['font-medium dark:text-gray-50', getFailuresClass(log.failures_since_start)]"
          >
            {{ log.failures_since_start }}
          </p>
        </div>
        <div v-if="isAccessAttempt" class="col-span-2">
          <h2 class="text-xl font-semibold mb-2 mt-4">Request Data</h2>
          <div class="bg-gray-100 p-4 rounded-md overflow-x-auto">
            <div v-if="log.get_data" class="mb-4">
              <h3 class="font-semibold text-lg mb-2">GET Data</h3>
              <pre class="bg-white p-2 rounded-md">{{
                formatJSON(log.get_data)
              }}</pre>
            </div>
            <div v-if="log.post_data">
              <h3 class="font-semibold text-lg mb-2">POST Data</h3>
              <pre class="bg-white p-2 rounded-md">{{
                formatJSON(log.post_data)
              }}</pre>
            </div>
          </div>
        </div>
        <div v-if="isAccessAttempt" class="col-span-2">
          <h2 class="text-xl font-semibold mb-2 mt-4">HTTP Accept</h2>
          <p class="font-medium">{{ log.http_accept || "N/A" }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  getAccessLogDetails,
  getAccessAttemptDetails,
} from "@/services/accessLogService";

export default {
  name: "AccessLogDetails",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const log = ref(null);
    const error = ref(null);
    const isLoading = ref(false);

    const isAccessAttempt = computed(
      () => route.name === "AccessAttemptDetails"
    );

    const fetchLogDetails = async () => {
      isLoading.value = true;
      try {
        const logId = route.params.id;
        const fetchFunction = isAccessAttempt.value
          ? getAccessAttemptDetails
          : getAccessLogDetails;
        log.value = await fetchFunction(logId);
        error.value = null;
      } catch (err) {
        console.error(
          `Error fetching ${
            isAccessAttempt.value ? "access attempt" : "access log"
          } details:`,
          err
        );
        error.value = `Failed to fetch ${
          isAccessAttempt.value ? "access attempt" : "access log"
        } details. Please try again later.`;
        log.value = null;
      } finally {
        isLoading.value = false;
      }
    };

    const formatDateTime = (dateString) => {
      const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    };

    const formatJSON = (data) => {
      try {
        return JSON.stringify(JSON.parse(data), null, 2);
      } catch {
        return data;
      }
    };

    const getFailuresClass = (failures) => {
      if (failures === 0) return "text-green-600";
      if (failures < 5) return "text-yellow-600";
      return "text-red-600";
    };

    const goBack = () => {
      router.push({ name: "AccessLogs" });
    };

    onMounted(fetchLogDetails);

    return {
      log,
      error,
      isLoading,
      isAccessAttempt,
      formatDateTime,
      formatJSON,
      getFailuresClass,
      goBack,
    };
  },
};
</script>
